import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Check, ChevronRight, ShoppingCart, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Motion";
import { API, money } from "@/lib/site";

const inputCls = "w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors";

export default function FlexBuilder() {
  const { type } = useParams();
  const isMaster = type === "master";
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [slaveTo, setSlaveTo] = useState("");
  const [master, setMaster] = useState({ company: "", details: "" });
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    document.title = `Build Your FLEX – ${isMaster ? "Master" : "Slave"} | Regal Tuning`;
    setSel(new Set());
    setSlaveTo("");
    setMaster({ company: "", details: "" });
    axios.get(`${API}/flex-config`).then((r) => setCfg(r.data)).catch(() => setErr(true));
  }, [type, isMaster]);

  if (!isMaster && type !== "slave") {
    return (
      <main className="grid min-h-[60vh] place-items-center pt-24">
        <Link to="/tuning-tools/magic-motorsport" className="text-regal-amber underline">Back to Magic Motorsport</Link>
      </main>
    );
  }
  if (err || !cfg) {
    return <main className="container-regal pt-40 pb-24"><div className="aspect-video animate-pulse border border-line bg-surface" /></main>;
  }

  const options = isMaster ? cfg.master_options : cfg.slave_options;
  const ccy = cfg.currency || "GBP";
  const hardware = cfg.hardware_price ?? 800;
  const linking = cfg.linking_charge ?? 0;
  const selArr = [...sel].sort((a, b) => a - b);
  const swTotal = selArr.reduce((a, i) => a + (options[i]?.price || 0), 0);
  const linkingApplied = !isMaster && slaveTo === "other" ? linking : 0;
  const total = hardware + swTotal + linkingApplied;
  const canAdd = sel.size > 0 && (isMaster || (slaveTo === "regal" || (slaveTo === "other" && master.company.trim())));

  const toggle = (i) => setSel((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const add = () => {
    const lines = selArr.map((i) => `${options[i].name} — ${money(options[i].price, ccy)}`);
    const linkLine = isMaster ? null : slaveTo === "regal"
      ? "Slave linked to Regal Tuning"
      : `Slave to: ${master.company.trim()}${master.details.trim() ? ` (${master.details.trim()})` : ""}`;
    const summary = [`FLEX hardware — ${money(hardware, ccy)}`, ...lines, linkLine, linkingApplied ? `Master linking — ${money(linkingApplied, ccy)}` : null].filter(Boolean).join("\n");
    addItem(
      { slug: `build-your-flex-${type}`, name: `Build Your FLEX – ${isMaster ? "Master" : "Slave"}`, image: "/assets/brands/magic-motorsport.webp", currency: ccy, badge: isMaster ? "MASTER" : "SLAVE" },
      1,
      {
        label: `Build Your FLEX – ${isMaster ? "Master" : "Slave"}`,
        currency: ccy,
        total,
        options: selArr,
        slave_to: isMaster ? undefined : slaveTo,
        master_company: master.company.trim(),
        master_details: master.details.trim(),
        summary,
      }
    );
    setOpen(true);
  };

  return (
    <main data-testid="flex-builder-page" className="pt-32 sm:pt-40 pb-24">
      <div className="container-regal">
        <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="breadcrumbs">
          <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-tools/magic-motorsport" className="hover:text-regal-amber">Magic Motorsport</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">Build Your FLEX</span>
        </nav>

        <Reveal>
          <p className="overline-label mb-3">Magic Motorsport Configurator</p>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink" data-testid="flex-builder-title">
            Build Your FLEX – {isMaster ? "Master" : "Slave"}
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-neutral-400">
            Start with the FLEX hardware, then add the software packages you need. {isMaster ? "Select one or multiple Master packages." : "Then choose who your Slave links to."} All prices in {ccy}.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <Reveal>
              <div className="flex items-center justify-between border border-line bg-white p-5" data-testid="flex-hardware-row">
                <div className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center bg-regal-amber text-white"><Check size={18} /></span>
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide text-ink">FLEX Hardware</p>
                    <p className="text-sm text-neutral-400">Included with every build</p>
                  </div>
                </div>
                <p className="font-display text-lg font-bold text-regal-amber">{money(hardware, ccy)}</p>
              </div>
            </Reveal>

            <div>
              <Reveal><p className="overline-label mb-5">{isMaster ? "Master" : "Slave"} software packages — select one or more</p></Reveal>
              <div className="space-y-3" data-testid="flex-options-list">
                {options.map((o, i) => {
                  const active = sel.has(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggle(i)}
                      data-testid={`flex-option-${i}`}
                      className={`flex w-full items-center justify-between gap-4 border p-4 sm:p-5 text-left transition-all duration-300 ${active ? "border-regal-amber bg-regal-amber/5" : "border-line bg-white hover:border-linebright"}`}
                    >
                      <span className="flex items-center gap-4 min-w-0">
                        <span className={`grid h-8 w-8 shrink-0 place-items-center border transition-colors ${active ? "border-regal-amber bg-regal-amber text-white" : "border-linebright text-transparent"}`}>
                          {active ? <Check size={15} /> : <Plus size={15} className="text-neutral-500" />}
                        </span>
                        <span className="text-sm font-medium text-ink leading-snug">{o.name}</span>
                      </span>
                      <span className="shrink-0 font-mono text-sm font-bold text-regal-amber">{money(o.price, ccy)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {!isMaster && (
              <Reveal>
                <div className="border border-line bg-white p-6 sm:p-7" data-testid="flex-slave-linking">
                  <p className="overline-label mb-5">Slave linking — required</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[["regal", "Slave to Regal Tuning", "Your FLEX Slave is linked to Regal Tuning as Master."], ["other", "Slave to Another Master", "Link to your own compatible FLEX Master."]].map(([val, t, d]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setSlaveTo(val)}
                        data-testid={`flex-slave-to-${val}`}
                        className={`border p-5 text-left transition-all duration-300 ${slaveTo === val ? "border-regal-amber bg-regal-amber/5" : "border-line hover:border-linebright"}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`grid h-5 w-5 place-items-center rounded-full border ${slaveTo === val ? "border-regal-amber" : "border-linebright"}`}>
                            {slaveTo === val && <span className="h-2.5 w-2.5 rounded-full bg-regal-amber" />}
                          </span>
                          <span className="font-display text-sm font-bold uppercase tracking-wide text-ink">{t}</span>
                        </span>
                        <span className="mt-2 block text-xs text-neutral-400 leading-relaxed">{d}</span>
                      </button>
                    ))}
                  </div>
                  {slaveTo === "other" && (
                    <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="flex-master-fields">
                      <label className="block">
                        <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Master company/name *</span>
                        <input value={master.company} onChange={(e) => setMaster({ ...master, company: e.target.value })} className={inputCls} data-testid="flex-master-company-input" />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Master FLEX details / serial (if applicable)</span>
                        <input value={master.details} onChange={(e) => setMaster({ ...master, details: e.target.value })} className={inputCls} data-testid="flex-master-details-input" />
                      </label>
                    </div>
                  )}
                </div>
              </Reveal>
            )}
          </div>

          <div>
            <div className="lg:sticky lg:top-28 border border-line bg-white p-6 sm:p-7 shadow-[0_16px_40px_rgba(16,24,40,0.08)]" data-testid="flex-summary">
              <p className="overline-label mb-5">Your configuration</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4"><span className="text-neutral-400">FLEX hardware</span><span className="font-mono text-ink">{money(hardware, ccy)}</span></div>
                {selArr.map((i) => (
                  <div key={i} className="flex justify-between gap-4" data-testid={`flex-summary-option-${i}`}>
                    <span className="text-neutral-400 leading-snug">{options[i].name}</span>
                    <span className="shrink-0 font-mono text-ink">{money(options[i].price, ccy)}</span>
                  </div>
                ))}
                {linkingApplied > 0 && (
                  <div className="flex justify-between gap-4"><span className="text-neutral-400">Master linking</span><span className="font-mono text-ink">{money(linkingApplied, ccy)}</span></div>
                )}
                {!isMaster && slaveTo && (
                  <p className="border-t border-line pt-3 text-xs text-neutral-400">
                    {slaveTo === "regal" ? "Slave linked to Regal Tuning" : `Slave to: ${master.company || "—"}`}
                  </p>
                )}
              </div>
              <div className="mt-6 border-t border-line pt-5">
                <div className="flex items-end justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Total</span>
                  <span className="font-display text-3xl font-extrabold text-regal-amber" data-testid="flex-total">{money(total, ccy)}</span>
                </div>
              </div>
              <button
                onClick={add}
                disabled={!canAdd}
                data-testid="flex-add-to-cart"
                className="mt-6 flex w-full items-center justify-center gap-2.5 bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ShoppingCart size={17} /> Add to Cart
              </button>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                {sel.size === 0 ? "Select at least one software package" : !isMaster && !slaveTo ? "Choose slave linking to continue" : ""}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
